import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { RootState, MyThunkDispatch } from 'store';
import styled from 'styled-components';


import { fetchHasAccess } from 'actions/accessLog';
import ConfirmationModal from 'components/ConfirmationModal';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import BranchCompanySelectList from 'screens/components/BillingCompanyList';
import {
  activateOrDesactivateBillingCompany,
  getAllBillingCompanies,
  setCompanyClientStatus,
} from 'services/billingCompanyService';
import { htmlT } from 'utils/generic';

const ModalText = styled.p`
  font-size: 16px;
  line-height: 1.5;
  text-align: center;
  margin: 0 0 16px;

  & > strong {
    font-weight: 700;
  }
`;

const ModalSubText = styled.p`
  font-size: 13px;
  line-height: 1.4;
  text-align: center;
  color: ${({ theme }) => theme.listPreviewCard.fontColor.empty};
  background: ${({ theme }) => theme.confirmationModal.buttonBackground};
  border-radius: 8px;
  padding: 12px;
  margin: 0;
`;

const BranchCompanyContainer = () => {
  const dispatch: MyThunkDispatch = useDispatch();
  const [billingCompanyList, setBillingCompanyList] = useState<GetAllBillingCompanyResponse[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [statusChangeTarget, setStatusChangeTarget] = useState<GetAllBillingCompanyResponse | undefined>();
  const [isStatusModalOpen, setStatusModalOpen] = useState(false);
  const toast = useToast();
  const { t } = useStoreTranslation();
  const companyId = useSelector((state: RootState) => state.user.accountResult?.companyId);
  const adminId = useSelector((state: RootState) => (state.user.accountResult ? state.user.accountResult?.id : ''));

  useEffect(() => {
    const screenName = 'ListBranchCompany';
    dispatch(fetchHasAccess(screenName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminId]);

  const getData = async () => {
    setLoading(true);
    try {
      const res = await getAllBillingCompanies(companyId ?? '');
      setBillingCompanyList(res);
    } catch (error) {
      toast.add({
        type: 'danger',
        message: t('branchCompany.list.errorFetch'),
      });
      setLoading(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = async (billingCompany: GetAllBillingCompanyResponse) => {
    try {
      setSaving(true);
      await activateOrDesactivateBillingCompany(companyId ?? '', billingCompany.id, !billingCompany.isActive);
      await getData();
    } catch (error) {
      toast.add({
        type: 'danger',
        message: t('branchCompany.list.errorToggle'),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRequestStatusChange = (billingCompany: GetAllBillingCompanyResponse) => {
    setStatusChangeTarget(billingCompany);
    setStatusModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    if (isSaving) return;
    setStatusModalOpen(false);
    setStatusChangeTarget(undefined);
  };

  const handleConfirmStatusChange = async () => {
    if (!statusChangeTarget) return;

    const willActivate = statusChangeTarget.isActive === false;

    try {
      setSaving(true);
      await setCompanyClientStatus(statusChangeTarget.id, willActivate);

      toast.add({
        type: 'success',
        message: t(willActivate ? 'branchCompany.list.activateSuccessToast' : 'branchCompany.list.deactivateSuccessToast'),
      });

      setStatusModalOpen(false);
      setStatusChangeTarget(undefined);
      await getData();
    } catch (error) {
      toast.add({
        type: 'danger',
        message: t('branchCompany.list.statusChangeErrorToast'),
      });
    } finally {
      setSaving(false);
    }
  };

  const isActivating = statusChangeTarget?.isActive === false;

  return (
    <>
      <BranchCompanySelectList
        billingCompanyList={billingCompanyList}
        isLoading={isLoading}
        isSaving={isSaving}
        onToggle={handleToggle}
        onToggleStatus={handleRequestStatusChange}
      />

      <ConfirmationModal
        open={isStatusModalOpen}
        title={t(isActivating ? 'branchCompany.list.confirmActivateTitle' : 'branchCompany.list.confirmDeactivateTitle')}
        onClose={handleCloseStatusModal}
        onConfirm={handleConfirmStatusChange}
        submitting={isSaving}
        disabled={isSaving}
        showCancelButton
        highlight
      >
        <>
          <ModalText>
            {htmlT(t, isActivating ? 'branchCompany.list.confirmActivateMessage' : 'branchCompany.list.confirmDeactivateMessage', {
              company: statusChangeTarget?.companyName ?? '',
            })}
          </ModalText>
          <ModalSubText>
            {t(isActivating ? 'branchCompany.list.confirmActivateSubMessage' : 'branchCompany.list.confirmDeactivateSubMessage')}
          </ModalSubText>
        </>
      </ConfirmationModal>
    </>
  );
};

export default BranchCompanyContainer;
